import { useState, useEffect, useCallback, useRef } from 'react';
import type { TelegramWebApp, TelegramUser } from '@/types/telegram';

type HapticImpact = 'light' | 'medium' | 'heavy';
type HapticNotify = 'success' | 'error' | 'warning';

/**
 * The Telegram Login Widget script (telegram-widget.js) injects
 * `window.Telegram.WebApp` even in a regular browser — but with empty
 * `initData` and `platform === 'unknown'`. So checking `!!window.Telegram?.WebApp`
 * gives a false positive and breaks every "browser vs in-Telegram" branch.
 *
 * A real Mini App container either passes a non-empty initData or reports
 * a concrete platform (`ios`, `android`, `tdesktop`, `macos`, `weba`, `web`).
 */
function isRealMiniApp(webApp: TelegramWebApp | undefined): boolean {
  if (!webApp) return false;
  const hasInitData = typeof webApp.initData === 'string' && webApp.initData.length > 0;
  const hasRealPlatform =
    typeof webApp.platform === 'string' &&
    webApp.platform.length > 0 &&
    webApp.platform !== 'unknown';
  return hasInitData || hasRealPlatform;
}

function safeIsVersionAtLeast(webApp: TelegramWebApp, version: string): boolean {
  try {
    return typeof webApp.isVersionAtLeast === 'function'
      ? webApp.isVersionAtLeast(version)
      : false;
  } catch {
    return false;
  }
}

export function useTelegram() {
  const [isReady, setIsReady] = useState(false);
  const [tg, setTg] = useState<TelegramWebApp | null>(null);
  const [user, setUser] = useState<TelegramUser | null>(null);
  const [isMiniApp, setIsMiniApp] = useState(false);
  const mainButtonCb = useRef<(() => void) | null>(null);
  const backButtonCb = useRef<(() => void) | null>(null);

  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    if (!webApp || !isRealMiniApp(webApp)) {
      setIsReady(true);
      return;
    }

    setTg(webApp);
    setIsMiniApp(true);
    if (webApp.initDataUnsafe?.user) {
      setUser(webApp.initDataUnsafe.user);
    }

    try {
      webApp.ready();
      webApp.expand();
    } catch {
      /* ignore */
    }

    // Color setters are version-gated. setHeaderColor/setBackgroundColor
    // landed in 6.1; setBottomBarColor in 7.10. Calling on older clients
    // generates `… is not supported in version X.Y` warnings.
    try {
      if (safeIsVersionAtLeast(webApp, '6.1')) {
        webApp.setHeaderColor('#0B0E14');
        webApp.setBackgroundColor('#0B0E14');
      }
      if (safeIsVersionAtLeast(webApp, '7.10')) {
        webApp.setBottomBarColor?.('#121620');
      }
    } catch {
      /* ignore */
    }

    setIsReady(true);

    return () => {
      try {
        if (mainButtonCb.current) webApp.MainButton.offClick(mainButtonCb.current);
        if (backButtonCb.current) webApp.BackButton.offClick(backButtonCb.current);
      } catch {
        /* ignore */
      }
    };
  }, []);

  const haptic = useCallback(
    (type: HapticImpact = 'light') => {
      try {
        tg?.HapticFeedback?.impactOccurred(type);
      } catch {
        /* noop */
      }
    },
    [tg]
  );

  const hapticNotify = useCallback(
    (type: HapticNotify) => {
      try {
        tg?.HapticFeedback?.notificationOccurred(type);
      } catch {
        /* noop */
      }
    },
    [tg]
  );

  const showMainButton = useCallback(
    (text: string, onClick: () => void) => {
      if (!tg) return;
      if (mainButtonCb.current) tg.MainButton.offClick(mainButtonCb.current);
      mainButtonCb.current = onClick;
      tg.MainButton.setText(text);
      tg.MainButton.setParams({
        color: '#00D4FF',
        text_color: '#0B0E14',
        is_active: true,
        is_visible: true,
      });
      tg.MainButton.onClick(onClick);
      tg.MainButton.show();
    },
    [tg]
  );

  const hideMainButton = useCallback(() => {
    if (!tg) return;
    if (mainButtonCb.current) {
      tg.MainButton.offClick(mainButtonCb.current);
      mainButtonCb.current = null;
    }
    tg.MainButton.hide();
  }, [tg]);

  const showBackButton = useCallback(
    (onClick: () => void) => {
      if (!tg) return;
      if (backButtonCb.current) tg.BackButton.offClick(backButtonCb.current);
      backButtonCb.current = onClick;
      tg.BackButton.onClick(onClick);
      tg.BackButton.show();
    },
    [tg]
  );

  const hideBackButton = useCallback(() => {
    if (!tg) return;
    if (backButtonCb.current) {
      tg.BackButton.offClick(backButtonCb.current);
      backButtonCb.current = null;
    }
    tg.BackButton.hide();
  }, [tg]);

  const sendData = useCallback(
    (data: Record<string, unknown>) => {
      try {
        tg?.sendData(JSON.stringify(data));
      } catch {
        /* noop */
      }
    },
    [tg]
  );

  return {
    isReady,
    tg,
    user,
    isInTelegram: isMiniApp,
    haptic,
    hapticNotify,
    showMainButton,
    hideMainButton,
    showBackButton,
    hideBackButton,
    sendData,
    initData: tg?.initData ?? '',
  };
}
